import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchMorphologyOverview } from '@/entities/beat';
import { EMITTER_CONSTANTS, KEY_RECOIL, VIEW_SOURCE_MODE_VALUE } from '@/shared/config';
import { useDeepCompareEffect, useEmitter } from '@/shared/lib/customHooks';
import emitter from '@/shared/lib/eventEmitter';
import { BULK_ACTION_ENUM } from '@/shared/model';
import type { IReportInfo } from '@/shared/model';
import {
  selectActiveTab,
  selectMorphologyTemplate,
  selectSelectedTemplate,
  selectTemplateChanges,
  useReportStore,
} from '@/entities/report';

/**
 * Feeds the morphology template overview column: fetching templates, keeping the selection consistent and merging in unsaved include edits. Fetches the whole-study overview once per beat type or a cell-scoped one when the source is the selected cells, names templates by rank with unclassified singled out, clears the selection on a background click or a cell-selection change, and re-resolves it after a reload triggered by RELOAD_TEMPLATE_OVERVIEW.
 *
 * @returns An object carrying the column ref, loading flag, merged template list, current selection and its click handler, the ECG data map, the view source and the reviewed-beat change lists.
 */
const useTemplateOverview = () => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  const reportInfo = useReportStore((s) => s.reportInfo) as IReportInfo;
  const ecgDataMap = useReportStore((s) => s.ecgDataMap);
  const templateChanges = useReportStore(selectTemplateChanges(keyRecoil));
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const viewBeatsSource = useReportStore((s) => s.viewBeatsSource);

  const morphologyTemplate = useReportStore(selectMorphologyTemplate(keyRecoil));
  const setMorphologyTemplateAction = useReportStore((s) => s.actions.setMorphologyTemplate);
  /**
   * Writes the cell-scoped morphology template list for the beats tab. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setMorphologyTemplate = (v: Parameters<typeof setMorphologyTemplateAction>[1]) =>
    setMorphologyTemplateAction(keyRecoil, v);
  const selectedTemplate = useReportStore(selectSelectedTemplate(keyRecoil));
  const setSelectedTemplateAction = useReportStore((s) => s.actions.setSelectedTemplate);
  /**
   * Replaces the morphology template selected in the overview. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedTemplate = (v: Parameters<typeof setSelectedTemplateAction>[1]) =>
    setSelectedTemplateAction(keyRecoil, v);
  /**
   * Reads the morphology template currently selected in the overview. Applies the tab-1 keyed selectedTemplate selector to a getState snapshot instead of subscribing to the store.
   *
   * @returns The selected morphology template, or an empty object when nothing is selected.
   */
  const getSelectedTemplate = () => selectSelectedTemplate(keyRecoil)(useReportStore.getState());
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Moves the beat-strip grid to another page. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const setBulkActionAction = useReportStore((s) => s.actions.setBulkAction);
  /**
   * Arms or clears the bulk action covering the beats tab. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setBulkAction = (v: Parameters<typeof setBulkActionAction>[1]) =>
    setBulkActionAction(keyRecoil, v);
  const setBulkActionByCellAction = useReportStore((s) => s.actions.setBulkActionByCell);
  /**
   * Arms or clears the bulk action scoped to the selected RR cells. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setBulkActionByCell = (v: Parameters<typeof setBulkActionByCellAction>[1]) =>
    setBulkActionByCellAction(keyRecoil, v);
  const resetControlModalAction = useReportStore((s) => s.actions.resetControlModal);
  /**
   * Closes the beat control modals belonging to the beats tab. Calls the store reset action with the tab-1 key already bound.
   *
   * @returns Nothing.
   */
  const resetControlModal = () => resetControlModalAction(keyRecoil);
  const setBulkActionSwapAction = useReportStore((s) => s.actions.setBulkActionSwap);
  /**
   * Queues or clears the bulk channel-swap action for the beats tab. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setBulkActionSwap = (v: Parameters<typeof setBulkActionSwapAction>[1]) =>
    setBulkActionSwapAction(keyRecoil, v);
  const listSelectedCells = useReportStore((s) => s.listSelectedCells);
  const originalTemplateOverview = useReportStore((s) => s.originalTemplateOverview);
  const setOriginalTemplateOverview = useReportStore((s) => s.actions.setOriginalTemplateOverview);
  /**
   * Reads the whole-study morphology template overview cached per beat type. Pulls originalTemplateOverview off a report-store snapshot rather than subscribing to it.
   *
   * @returns The template overview keyed by beat type.
   */
  const getOriginalTemplateOverview = () => useReportStore.getState().originalTemplateOverview;
  const bulkReviewedBeats = useReportStore((s) => s.bulkReviewedBeats);
  const resetBulkReviewedBeats = useReportStore((s) => s.resetBulkReviewedBeats);
  const reviewedBeatsChanges = useReportStore((s) => s.reviewedBeatsChanges);
  /**
   * Reads which beat types have already had their template overview fetched. Pulls initTemplateOverview off a report-store snapshot rather than subscribing to it.
   *
   * @returns The list of beat types already initialised.
   */
  const getInitTemplateOverview = () => useReportStore.getState().initTemplateOverview;
  const setInitTemplateOverview = useReportStore((s) => s.actions.setInitTemplateOverview);

  const [isLoadingTemplate, setLoadingTemplate] = useState(false);

  const columnContentRef = useRef<HTMLDivElement>(null);
  const shouldReSelectTemplate = useRef(false);
  const preventReSelectTemplate = useRef(false);

  const { studyId, profileId } = reportInfo || {};

  /**
   * Loads the morphology template overview for the active beat type across the whole study. Calls fetchMorphologyOverview with the study and profile ids, records the beat type as initialised, names each template by rank with unclassified ones labelled as such while keeping a copy of the original data, raises the re-select flag on a reload, and logs and swallows any failure while always clearing the loading flag.
   *
   * @param isReload - True when refetching, so the current selection is resolved again afterwards.
   * @returns A promise resolving once the overview has been stored or the failure logged.
   */
  const handleFetchOriginalTemplate = async (isReload?: boolean) => {
    setLoadingTemplate(true);
    try {
      const input = {
        studyId,
        profileId,
        templateType: activeButton,
      };
      const { morphologyOverview } = await fetchMorphologyOverview(input);
      setInitTemplateOverview((prev: any) => {
        const clone = _.cloneDeep(prev || []);
        if (!_.includes(clone, activeButton)) {
          clone.push(activeButton);
        } else {
          return prev;
        }
        return clone;
      });

      const formatPcvMorphology = _.map(morphologyOverview, (x, i) => ({
        ...x,
        originalData: _.cloneDeep(x),
        name: x.unclassified ? 'Unclassified' : `#${i + 1}`,
      }));
      if (isReload) {
        shouldReSelectTemplate.current = true;
      }
      setOriginalTemplateOverview((prev: any) => ({
        ...prev,
        [activeButton]: formatPcvMorphology,
      }));
    } catch (error) {
      console.log('Failed to fetch template', error);
    } finally {
      setLoadingTemplate(false);
    }
  };

  /**
   * Loads the morphology templates restricted to the RR cells the user has selected. Calls fetchMorphologyOverview with the cell ids, sorts the result so unclassified comes last and the rest follow their order in the whole-study overview, reuses that overview's names where the template is already known, and logs and swallows any failure while always clearing the loading flag.
   *
   * @param listSelectedCells - Selected heat-map cells whose ids scope the request.
   * @returns A promise resolving once the cell-scoped templates have been stored.
   */
  const handleFetchTemplate = async (listSelectedCells: any[]) => {
    setLoadingTemplate(true);
    try {
      const input = {
        studyId,
        profileId,
        rrCellIds: _.map(listSelectedCells, (x) => x.id) || [],
        templateType: activeButton,
      };
      const { morphologyOverview } = await fetchMorphologyOverview(input);
      const originalTemplate = getOriginalTemplateOverview()[activeButton] || [];
      const sortedSelectedEventInfo = _.sortBy(morphologyOverview, [
        (template: any) => template.unclassified,
        (template: any) =>
          originalTemplate.findIndex((t: any) => t.id === template.id) !== -1
            ? originalTemplate.findIndex((t: any) => t.id === template.id)
            : 9999,
      ]);
      const formatPcvMorphology = _.map(sortedSelectedEventInfo, (x, i) => ({
        ...x,
        originalData: _.cloneDeep(x),
        name: x.unclassified
          ? 'Unclassified'
          : originalTemplate.find((t: any) => t.id === x.id)?.name || `#${i + 1}`,
      }));
      setMorphologyTemplate(formatPcvMorphology);
    } catch (error) {
      console.log('Failed to fetch template', error);
    } finally {
      setLoadingTemplate(false);
    }
  };

  /**
   * Selects a morphology template and resets everything scoped to the previous one. Stores the template, rewinds the beat page to zero, clears both bulk actions, the control modals and any queued swap action, and when the view source is all templates emits REMOVE_SELECTED_CELLS, additionally suppressing the automatic re-select while cells remain selected.
   *
   * @param item - Morphology template behind the clicked preview.
   * @returns Nothing.
   */
  const onClickSelect = (item: any) => {
    setSelectedTemplate(item);
    setPageIndex((prev) => (prev.index === 0 ? prev : { index: 0 }));
    setBulkAction(BULK_ACTION_ENUM.NOTHING);
    setBulkActionByCell(BULK_ACTION_ENUM.NOTHING);
    resetControlModal();
    setBulkActionSwap((prev: any) => {
      if (_.isEmpty(prev)) return prev;
      return [];
    });
    if (viewBeatsSource.value === VIEW_SOURCE_MODE_VALUE.ALL) {
      emitter.emit(EMITTER_CONSTANTS.REMOVE_SELECTED_CELLS);
    }
    if (viewBeatsSource.value === VIEW_SOURCE_MODE_VALUE.ALL && !_.isEmpty(listSelectedCells)) {
      preventReSelectTemplate.current = true;
    }
  };

  /**
   * Clears the template selection when a plain left-click lands on the column background. Ignores anything but a primary-button press and any click carrying ctrl, meta or shift, then walks the template-preview children to see whether the target sat inside one and empties the selection only when it did not.
   *
   * @param event - Mousedown event on the template column.
   * @returns Nothing.
   */
  const onClickElement = (event: any) => {
    event.stopPropagation();
    event.preventDefault();

    if (![1].includes(event?.buttons)) return;
    if (event && !event?.ctrlKey && !event?.metaKey && !event?.shiftKey) {
      // Click inside container to clear
      const elements = columnContentRef.current;
      const childrens = elements.getElementsByClassName('template-preview-container');
      let isClickInside = false;
      _.forEach(childrens, (element) => {
        if (element) {
          if (element.contains(event.target)) {
            isClickInside = true;
          }
        }
      });
      if (!isClickInside) {
        setSelectedTemplate((prev: any) => {
          if (_.isEmpty(prev)) return prev;
          return {};
        });
      }
    }
  };

  useEffect(() => {
    const initTemplateOverView = _.cloneDeep(getInitTemplateOverview() || []);
    if (
      !_.includes(initTemplateOverView, activeButton) &&
      activeButton &&
      viewBeatsSource.value === VIEW_SOURCE_MODE_VALUE.ALL
    ) {
      handleFetchOriginalTemplate();
    }
    if (
      viewBeatsSource.value === VIEW_SOURCE_MODE_VALUE.SELECTED &&
      !_.isEmpty(listSelectedCells)
    ) {
      handleFetchTemplate(listSelectedCells);
    }
  }, [activeButton, viewBeatsSource, listSelectedCells]);

  useDeepCompareEffect(() => {
    if (preventReSelectTemplate.current) {
      preventReSelectTemplate.current = false;
      return;
    }
    setSelectedTemplate({});
  }, [listSelectedCells]);

  useEffect(() => {
    const elements = columnContentRef.current;
    elements.addEventListener('mousedown', onClickElement);
    return () => elements.removeEventListener('mousedown', onClickElement);
  }, []);

  const mergeTemplateList = useMemo(() => {
    if (viewBeatsSource.value === VIEW_SOURCE_MODE_VALUE.SELECTED && _.isEmpty(listSelectedCells))
      return [];
    if (viewBeatsSource.value === VIEW_SOURCE_MODE_VALUE.ALL) {
      const morphology = originalTemplateOverview[activeButton] || [];
      return _.map(morphology, (x) => {
        const findChange = _.find(templateChanges, (change) => x.id === change.id);
        return findChange || x;
      });
    }
    return _.map(morphologyTemplate, (x) => {
      const findChange = _.find(templateChanges, (change) => x.id === change.id);
      return findChange || x;
    });
  }, [
    morphologyTemplate,
    templateChanges,
    activeButton,
    originalTemplateOverview,
    viewBeatsSource,
    listSelectedCells,
  ]);

  useEffect(() => {
    if (shouldReSelectTemplate.current) {
      const currnetTemplate = getSelectedTemplate();
      if (currnetTemplate && currnetTemplate.id) {
        const newSelected = _.find(mergeTemplateList, (x) => x.id === currnetTemplate.id);
        if (newSelected) {
          setSelectedTemplate(newSelected);
        } else {
          setSelectedTemplate({});
        }
      } else {
        setSelectedTemplate({});
      }
      shouldReSelectTemplate.current = false;
    }
  }, [mergeTemplateList, selectedTemplate]);

  useEmitter(
    EMITTER_CONSTANTS.RELOAD_TEMPLATE_OVERVIEW,
    () => {
      const initTemplateOverView = _.cloneDeep(getInitTemplateOverview() || []);
      if (
        !_.includes(initTemplateOverView, activeButton) &&
        activeButton &&
        viewBeatsSource.value === VIEW_SOURCE_MODE_VALUE.ALL
      ) {
        handleFetchOriginalTemplate(true);
      }
    },
    [activeButton, viewBeatsSource, listSelectedCells],
  );

  return {
    columnContentRef,
    isLoadingTemplate,
    mergeTemplateList,
    selectedTemplate,
    onClickSelect,
    ecgDataMap,
    viewBeatsSource,
    reviewedBeatsChanges,
    bulkReviewedBeats,
    resetBulkReviewedBeats,
  };
};

export default useTemplateOverview;
